import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useTeamsAnalytics } from "../hooks/useTeamsAnalytics";
import {
  REVENUE_CHART_CARD_PADDING,
  REVENUE_CHART_CARD_SHADOW,
} from "./revenue/constants";
import { RevenueChartCard } from "./revenue/RevenueChartCard";
import { SERVICES_CONTENT_GAP } from "./services/constants";
import { TeamEmployeeOfMonthSection } from "./team/TeamEmployeeOfMonthSection";
import { TeamKpiCards } from "./team/TeamKpiCards";
import { DEFAULT_TEAM_SEGMENT, parseTeamSegment } from "./team/mockData";
import {
  findTeamRowById,
  buildTeamKpiComparison,
  mapStaffTopRows,
  mapTeamTopRows,
  mapTeamsSegmentData,
} from "./team/teamsTabMappers";
import {
  downloadTeamsDetailExport,
  downloadTeamsTabExport,
} from "./team/teamsTabExport";
import {
  mapAvgTimeSeriesToChartPoints,
  mapStaffSeriesToChartPoints,
} from "./team/teamsDetailMappers";
import { TeamSegmentPills } from "./team/TeamSegmentPills";
import { TeamStaffTasksTimeChart } from "./team/TeamStaffTasksTimeChart";
import { TeamDetailView } from "./team/TeamDetailView";
import { TeamStaffDetailView } from "./team/TeamStaffDetailView";
import { TeamStaffPerformanceTable } from "./team/TeamStaffPerformanceTable";
import type { TeamSegment, TeamStaffRow } from "./team/types";

const RANKING_PREVIEW_LIMIT: Record<TeamSegment, number> = {
  staff: 1,
  teams: 3,
};

function getStaffSeriesId(
  segment: TeamSegment,
  staffId: string | null,
): string | undefined {
  return segment === "staff" && staffId ? staffId : undefined;
}

export const TeamTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();

  const segment = parseTeamSegment(searchParams.get("teamSegment"));
  const staffId = searchParams.get("staffId");
  const teamId = searchParams.get("teamId");
  const detailId = segment === "teams" ? teamId : staffId;
  const deferredSegment = useDeferredValue(segment);
  const isSegmentPending = segment !== deferredSegment;
  const [isRankingExpanded, setIsRankingExpanded] = useState(false);

  const teamsAnalytics = useTeamsAnalytics(
    dateRange,
    getStaffSeriesId(segment, staffId),
  );
  const isContentPending =
    isSegmentPending || teamsAnalytics.isLoading || teamsAnalytics.isFetching;

  const canExport =
    teamsAnalytics.isReady &&
    !teamsAnalytics.isLoading &&
    !teamsAnalytics.isError;

  const staffRows = useMemo(
    () =>
      mapStaffTopRows(
        teamsAnalytics.topStaff.data?.entity === "STAFF"
          ? teamsAnalytics.topStaff.data.rows
          : undefined,
      ),
    [teamsAnalytics.topStaff.data],
  );

  const teamRows = useMemo(
    () =>
      mapTeamTopRows(
        teamsAnalytics.topTeam.data?.entity === "TEAM"
          ? teamsAnalytics.topTeam.data.rows
          : undefined,
      ),
    [teamsAnalytics.topTeam.data],
  );

  const data = useMemo(
    () =>
      mapTeamsSegmentData(
        teamsAnalytics.overview.data,
        staffRows,
        teamRows,
        deferredSegment,
      ),
    [deferredSegment, staffRows, teamRows, teamsAnalytics.overview.data],
  );

  const kpiComparison = useMemo(
    () =>
      buildTeamKpiComparison(
        teamsAnalytics.overview.data,
        teamsAnalytics.previousOverview.data,
        deferredSegment,
      ),
    [
      deferredSegment,
      teamsAnalytics.overview.data,
      teamsAnalytics.previousOverview.data,
    ],
  );

  const rankingPreviewLimit = RANKING_PREVIEW_LIMIT[deferredSegment];
  const rankingTableRows = useMemo(
    () =>
      isRankingExpanded
        ? data.staffRows
        : data.staffRows.slice(0, rankingPreviewLimit),
    [data.staffRows, isRankingExpanded, rankingPreviewLimit],
  );
  const shouldShowRankingViewAll =
    !isRankingExpanded && data.staffRows.length > rankingTableRows.length;

  const selectedEntity = useMemo(
    () =>
      detailId
        ? findTeamRowById(data.topThree, data.staffRows, detailId)
        : undefined,
    [detailId, data.staffRows, data.topThree],
  );

  const handleViewAllRanking = useCallback(() => {
    setIsRankingExpanded(true);
  }, []);

  const avgTimeChartData = useMemo(
    () =>
      mapAvgTimeSeriesToChartPoints(
        teamsAnalytics.overview.data?.avgTimeSeries,
      ),
    [teamsAnalytics.overview.data?.avgTimeSeries],
  );

  const staffSeriesChartData = useMemo(
    () => mapStaffSeriesToChartPoints(teamsAnalytics.staffSeries.data?.series),
    [teamsAnalytics.staffSeries.data?.series],
  );

  const handleExportTab = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!canExport) {
        return;
      }

      try {
        await downloadTeamsTabExport({
          hotelId: teamsAnalytics.hotelId,
          from: teamsAnalytics.from,
          to: teamsAnalytics.to,
          segment: deferredSegment,
          format,
          summary: data.summary,
          rows: [...data.topThree, ...data.staffRows],
          avgTimeSeries: teamsAnalytics.overview.data?.avgTimeSeries ?? [],
          labels: {
            summaryTitle: t("analytics.team.export.summaryTitle", "Summary"),
            metric: t("analytics.team.export.metric", "Metric"),
            value: t("analytics.team.export.value", "Value"),
            performanceTitle: t(
              "analytics.team.performanceTitle",
              "Team Performance",
            ),
            rank: t("analytics.common.rate", "Rate"),
            name:
              deferredSegment === "teams"
                ? t("analytics.team.teamColumn", "Team")
                : t("analytics.common.name", "Name"),
            tasksCompleted: t(
              "analytics.team.table.tasksCompleted",
              "Tasks completed",
            ),
            tasksCancelled: t(
              "analytics.team.table.tasksCancelled",
              "Tasks cancelled",
            ),
            avgTimeOnTask: t(
              "analytics.team.table.avgTimeOnTask",
              "Avg time on task",
            ),
            delays: t("analytics.team.table.delays", "Delays"),
            dayOff: t("analytics.team.table.dayOff", "Day off"),
            dailyAverageTitle: t(
              "analytics.team.export.dailyAverageTitle",
              "Daily average time",
            ),
            day: t("analytics.team.export.day", "Day"),
            taskCount: t("analytics.team.export.taskCount", "Task count"),
            avgSeconds: t("analytics.team.export.avgSeconds", "Avg seconds"),
          },
        });
        toast.success(
          t(
            "analytics.team.toast.exportSuccess",
            "Team analytics export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.team.toast.exportError",
            "Failed to export team analytics",
          ),
        );
      }
    },
    [
      canExport,
      deferredSegment,
      data.staffRows,
      data.summary,
      data.topThree,
      teamsAnalytics.from,
      teamsAnalytics.hotelId,
      teamsAnalytics.overview.data?.avgTimeSeries,
      teamsAnalytics.to,
      t,
    ],
  );

  const handleDetailExport = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!canExport || !detailId) {
        return;
      }

      try {
        await downloadTeamsDetailExport({
          hotelId: teamsAnalytics.hotelId,
          from: teamsAnalytics.from,
          to: teamsAnalytics.to,
          segment: deferredSegment,
          format,
        });
        toast.success(
          t(
            "analytics.team.toast.detailExportSuccess",
            "Team detail export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.team.toast.detailExportError",
            "Failed to export team detail",
          ),
        );
      }
    },
    [
      canExport,
      deferredSegment,
      detailId,
      teamsAnalytics.from,
      teamsAnalytics.hotelId,
      teamsAnalytics.to,
      t,
    ],
  );

  const handleGlobalExport = useCallback(
    async (format: "csv" | "xlsx") => {
      if (detailId && selectedEntity) {
        await handleDetailExport(format);
        return;
      }

      await handleExportTab(format);
    },
    [detailId, handleDetailExport, handleExportTab, selectedEntity],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport,
      onExport: handleGlobalExport,
    }),
    [canExport, handleGlobalExport],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  useEffect(() => {
    if (!detailId || selectedEntity) {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.delete("staffId");
      nextParams.delete("teamId");
      return nextParams;
    });
  }, [detailId, selectedEntity, setSearchParams]);

  useEffect(() => {
    setIsRankingExpanded(false);
  }, [
    deferredSegment,
    teamsAnalytics.from,
    teamsAnalytics.hotelId,
    teamsAnalytics.to,
  ]);

  const handleSegmentChange = useCallback(
    (value: TeamSegment) => {
      setSearchParams((previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.set("tab", "team");
        nextParams.delete("staffId");
        nextParams.delete("teamId");

        if (value === DEFAULT_TEAM_SEGMENT) {
          nextParams.delete("teamSegment");
        } else {
          nextParams.set("teamSegment", value);
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const handleRowClick = useCallback(
    (row: TeamStaffRow) => {
      setSearchParams((previousParams) => {
        const nextParams = new URLSearchParams(previousParams);
        nextParams.set("tab", "team");

        if (segment === "teams") {
          nextParams.set("teamId", row.id);
          nextParams.delete("staffId");
        } else {
          nextParams.set("staffId", row.id);
          nextParams.delete("teamId");
        }

        return nextParams;
      });
    },
    [segment, setSearchParams],
  );

  const handleBackFromDetail = useCallback(() => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.delete("staffId");
      nextParams.delete("teamId");
      return nextParams;
    });
  }, [setSearchParams]);

  if (detailId && selectedEntity) {
    if (deferredSegment === "teams") {
      return (
        <TeamDetailView
          team={selectedEntity}
          onBack={handleBackFromDetail}
          onExport={handleDetailExport}
          exportDisabled={!canExport}
        />
      );
    }

    return (
      <TeamStaffDetailView
        staff={selectedEntity}
        segment={deferredSegment}
        onBack={handleBackFromDetail}
        onExport={handleDetailExport}
        exportDisabled={!canExport}
        chartData={staffSeriesChartData}
      />
    );
  }

  let mainContent: React.ReactNode;

  if (!teamsAnalytics.isReady) {
    mainContent = (
      <RevenueChartCard
        title={t("analytics.team.title", "Team")}
        className="w-full"
        showExport={false}
      >
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.team.missingFilters",
            "Select a hotel and date range to view team analytics.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else if (teamsAnalytics.isError) {
    mainContent = (
      <RevenueChartCard
        title={t("analytics.team.title", "Team")}
        className="w-full"
        showExport={false}
      >
        <p className="text-sm text-[#737373]">
          {t(
            "analytics.team.loadError",
            "Unable to load team analytics. Try again.",
          )}
        </p>
      </RevenueChartCard>
    );
  } else {
    mainContent = (
      <>
        <TeamKpiCards
          summary={data.summary}
          segment={deferredSegment}
          comparisonByKey={kpiComparison}
        />

        {avgTimeChartData.length > 0 ? (
          <TeamStaffTasksTimeChart data={avgTimeChartData} />
        ) : null}

        <div
          className={cn(
            "flex w-full flex-col gap-4 rounded-2xl border border-[#E5E5E5] bg-white font-sans",
            REVENUE_CHART_CARD_SHADOW,
          )}
          style={{ padding: REVENUE_CHART_CARD_PADDING }}
        >
          <TeamEmployeeOfMonthSection
            topThree={data.topThree}
            segment={deferredSegment}
            embedded
            onPodiumClick={handleRowClick}
          />

          <TeamStaffPerformanceTable
            rows={rankingTableRows}
            segment={deferredSegment}
            embedded
            onViewAll={
              shouldShowRankingViewAll ? handleViewAllRanking : undefined
            }
            onRowClick={handleRowClick}
          />
        </div>
      </>
    );
  }

  return (
    <section className="space-y-6 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TeamSegmentPills value={segment} onChange={handleSegmentChange} />
      </div>

      <div
        className={cn(
          "flex w-full max-w-full flex-col transition-opacity duration-150",
          isContentPending && "opacity-70",
        )}
        style={{ gap: SERVICES_CONTENT_GAP }}
      >
        {mainContent}
      </div>
    </section>
  );
};
